import React, { useMemo, useState, useEffect } from "react";
import natureBg from "./img/Foto2wilmanatur.jpg";
import "./css/toDo.css";
import { useNavigate } from "react-router-dom";
import { getTodos, createTodo, updateTodo, deleteTodo } from "./api/toDoApi";
import { useLang } from "./hooks/useLang";
import { DICT } from "./i18n";

function ToDo() {
  const navigate = useNavigate();

  const [lang] = useLang();
  const t = DICT[lang] ?? DICT.sv;

  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    getTodos().then(setTasks).catch(console.error);
  }, []);

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [author, setAuthor] = useState("");
  const [urgency, setUrgency] = useState(3);

  const [sortByUrgency, setSortByUrgency] = useState(true);

  const displayedTasks = useMemo(() => {
    const copy = [...tasks];
    if (sortByUrgency) {
      copy.sort((a, b) => {
        if (b.urgency !== a.urgency) return b.urgency - a.urgency;
        return Number(a.done) - Number(b.done);
      });
    }
    return copy;
  }, [tasks, sortByUrgency]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;

    const created = await createTodo({
      title: trimmedTitle,
      description: description.trim(),
      author: author.trim() || t.unknown,
      urgency: Number(urgency),
      done: false,
    });

    setTasks((prev) => [...prev, created]);

    setTitle("");
    setDescription("");
    setAuthor("");
    setUrgency(3);
    setShowForm(false);
  };

  const toggleDone = async (id) => {
    const current = tasks.find((x) => x.id === id);
    if (!current) return;

    const updated = await updateTodo(id, { done: !current.done });
    setTasks((prev) => prev.map((x) => (x.id === id ? updated : x)));
  };

  const handleDelete = async (id) => {
    await deleteTodo(id);
    setTasks((prev) => prev.filter((x) => x.id !== id));
  };

  return (
    <div className="page">
      <div className="page-bg" style={{ backgroundImage: `url(${natureBg})` }} />
      <div className="page-content todo-page">
        <header className="todo-header" onClick={() => navigate("/homelogin")}>
          Scoutkåren Vasa Korsfarare
        </header>

        <aside className="todo-sidebar">
          <img
            src={require("./img/treetop.png")}
            alt="tree top"
            className="todo-sidebar-tree"
          />

          <div className="todo-sidebar-logo-wrapper">
            <img
              src={require("./img/svk.png")}
              alt="logo"
              className="todo-sidebar-logo"
            />
            <div className="todo-sidebar-logo-bubble">{t.welcome}</div>
          </div>

          <button className="todo-menu-btn" onClick={() => navigate("/file")}>
            {t.files}
          </button>
          <button className="todo-menu-btn" onClick={() => navigate("/todo")}>
            {t.todo}
          </button>
          <button className="todo-menu-btn" onClick={() => navigate("/inventory")}>
            {t.inventory}
          </button>
          <button className="todo-menu-btn" onClick={() => navigate("/homelogin")}>
            {t.back}
          </button>
        </aside>

        <main className="todo-content">
          <h2 className="todo-title">{t.todoTitle}</h2>

          <div className="todo-controls">
            <button
              type="button"
              className="todo-new-btn"
              onClick={() => setShowForm((v) => !v)}
            >
              {showForm ? t.toggleClose : t.toggleCreate}
            </button>

            <label className="todo-sort-toggle">
              <input
                type="checkbox"
                checked={sortByUrgency}
                onChange={(e) => setSortByUrgency(e.target.checked)}
              />
              {t.sortByPrio}
            </label>

            <span className="todo-file-count">
              {tasks.length} {t.items}
            </span>
          </div>

          {showForm && (
            <div className="todo-form-pop">
              <form className="todo-form" onSubmit={handleAdd}>
                <div className="todo-form-row">
                  <input
                    className="todo-input"
                    type="text"
                    placeholder={t.titlePh}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    autoFocus
                  />
                </div>

                <div className="todo-form-row">
                  <textarea
                    className="todo-textarea"
                    placeholder={t.descPh}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                  />
                </div>

                <div className="todo-form-row todo-form-row-split">
                  <input
                    className="todo-input"
                    type="text"
                    placeholder={t.namePh}
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                  />

                  <div className="todo-urgency">
                    <label className="todo-urgency-label">
                      {t.prioLabel}: <strong>{urgency}</strong>
                    </label>
                    <input
                      className="todo-range"
                      type="range"
                      min="1"
                      max="5"
                      value={urgency}
                      onChange={(e) => setUrgency(e.target.value)}
                    />
                  </div>
                </div>

                <div className="todo-form-row todo-form-actions">
                  <button className="todo-add-btn" type="submit">
                    {t.add}
                  </button>

                  <button
                    className="todo-cancel-btn"
                    type="button"
                    onClick={() => setShowForm(false)}
                  >
                    {t.cancel}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="todo-card">
            {displayedTasks.length === 0 ? (
              <p className="todo-empty">{t.empty}</p>
            ) : (
              <ul className="todo-list">
                {displayedTasks.map((task) => (
                  <li key={task.id} className="todo-item todo-item-extended">
                    <label className="todo-item-main">
                      <input
                        type="checkbox"
                        checked={task.done}
                        onChange={() => toggleDone(task.id)}
                      />

                      <div className="todo-item-textblock">
                        <div className="todo-item-top">
                          <span className={"todo-text" + (task.done ? " done" : "")}>
                            {task.title}
                          </span>

                          <span className="todo-badges">
                            <span className="todo-badge">P: {task.urgency}</span>
                            <span className="todo-badge">
                              {t.creator}: {task.author}
                            </span>
                          </span>
                        </div>

                        {task.description && (
                          <div className={"todo-desc" + (task.done ? " done" : "")}>
                            {task.description}
                          </div>
                        )}
                      </div>
                    </label>

                    <button
                      className="todo-delete-btn"
                      type="button"
                      onClick={() => handleDelete(task.id)}
                    >
                      {t.remove}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default ToDo;